import {CommonModule} from '@angular/common';
import {Component} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {SupportedLanguage, TranslationService} from '@emma-labs/shared/angular';
import {fullName, Profile, SupportedLanguageCode, UserRole} from '@emma-labs/shared/typescript';
import {MenuItem} from 'primeng/api';
import {ButtonModule} from 'primeng/button';
import {TabMenuModule} from 'primeng/tabmenu';
import {first, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {AuthService} from '../../../services/auth.service';
import {BrokerProfileService} from '../../../services/broker-profile/broker-profile.service';

@Component({
  selector: 'em-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [CommonModule, ButtonModule, TabMenuModule],
})
export class HeaderComponent {
  items: MenuItem[] | undefined;
  brokerName$: Observable<string>;
  currentLanguage: SupportedLanguage;
  supportedLanguage = SupportedLanguage;
  private userRole?: UserRole;

  constructor(
    private readonly profileService: BrokerProfileService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly authService: AuthService,
    private readonly translate: TranslationService,
  ) {
    this.authService.currentAgentSessionStream.pipe(first()).subscribe((session) => {
      this.userRole = session.agent.role;

      this.items = [
        {
          label: this.translate.getTranslation('Espace tarification'),
          icon: 'pi pi-fw pi-home',
          routerLink: ['/', 'dashboard', 'underwriting', 'search'],
          visible:
            session.agent.role === UserRole.UNDERWRITER || session.agent.role === UserRole.CASE_MANAGER,
        },
        {
          label: this.translate.getTranslation('Ventes'),
          icon: 'pi pi-fw pi-table',
          routerLink: ['/', 'dashboard', 'proposals'],
          visible: this.isAdvisorOrSuperAdvisor(session.agent.role),
        },
        {
          label: this.translate.getTranslation('Profil'),
          icon: 'pi pi-fw pi-user',
          routerLink: ['/', 'dashboard', 'profile'],
          visible: this.isAdvisorOrSuperAdvisor(session.agent.role),
        },
        {
          label: this.translate.getTranslation('Documentation'),
          icon: 'pi pi-fw pi-file',
          routerLink: ['/', 'dashboard', 'documentation'],
          visible: this.isAdvisorOrSuperAdvisor(session.agent.role),
        },
        {
          label: this.translate.getTranslation('Recherche client'),
          icon: 'pi pi-fw pi-search',
          routerLink: ['/', 'dashboard', 'client-search'],
          visible: this.isAdvisorOrSuperAdvisor(session.agent.role),
        },
        {
          label: this.translate.getTranslation('Rapports de vente'),
          icon: 'pi pi-fw pi-folder-open',
          routerLink: ['/', 'dashboard', 'sale-report-search'],
          visible: this.isAdvisorOrSuperAdvisor(session.agent.role),
        },
        {
          label: this.translate.getTranslation('Soumission rapide'),
          icon: 'pi pi-fw pi-th-large',
          routerLink: ['/', 'dashboard', 'quick-quote'],
          visible: this.isAdvisorOrSuperAdvisor(session.agent.role),
        },
        {
          label: this.translate.getTranslation('Comparateur de soumission'),
          icon: 'pi pi-fw pi-tags',
          routerLink: ['/', 'dashboard', 'quote-comparator'],
          visible: this.isAdvisorOrSuperAdvisor(session.agent.role),
        },
      ];
    });

    this.brokerName$ = this.profileService
      .getProperty((profile: Profile) => ({
        firstName: profile.firstName,
        lastName: profile.lastName,
      }))
      .pipe(map(fullName));

    this.currentLanguage = this.translate.getCurrentLanguage();
  }

  startProposal(): void {
    this.router.navigate(['/', 'dashboard', 'proposal']);
  }

  isStartProposalButtonDisplayed(): boolean {
    const firstPathSegment = this.route.snapshot.url[0].path;
    return (
      !['proposal'].includes(firstPathSegment) &&
      !!this.userRole &&
      this.isAdvisorOrSuperAdvisor(this.userRole)
    );
  }

  logout(): void {
    this.authService.logout().pipe(first()).subscribe();
  }

  languageRedirect() {
    const languageCodeRedirect: SupportedLanguageCode =
      this.currentLanguage === SupportedLanguage.EN ? 'fr' : 'en';

    window.location.replace(
      `${location.protocol}//${location.host}/${languageCodeRedirect}${location.pathname.slice(3)}`,
    );
  }

  private isAdvisorOrSuperAdvisor(role: UserRole): boolean {
    return role === UserRole.ADVISOR || role === UserRole.SUPER_ADVISOR;
  }
}
